/**
 * Currency formatting utilities
 * 
 * This module provides consistent currency formatting, normalization, and comparison
 * functions for use throughout the application. All currency values are formatted
 * as USD with dollar sign, comma separators, and two decimal places.
 * 
 * @module currency
 */

// Standard currency formatter
const formatMoney = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
	minimumFractionDigits: 2
});

/**
 * Format a value as currency consistently
 * 
 * This function ensures all currency values are displayed with consistent formatting:
 * - Dollar sign prefix
 * - Comma separators for thousands
 * - Two decimal places
 * - Handles both raw numbers and already formatted strings
 * 
 * @param {string|number} value - The value to format
 * @returns {string} Formatted currency string (e.g., "$1,234.56") or empty string if invalid
 * 
 * @example
 * // Format raw numbers
 * formatCurrencyValue(1234.56); // "$1,234.56"
 * formatCurrencyValue("1234.56"); // "$1,234.56"
 * 
 * // Already formatted values are returned as-is
 * formatCurrencyValue("$1,234.56"); // "$1,234.56"
 * 
 * // Handle invalid values
 * formatCurrencyValue(null); // ""
 * formatCurrencyValue("invalid"); // ""
 */
export const formatCurrencyValue = (value) => {
	if (!value) return '';
	
	// If value is already formatted as currency (contains $), return as is
	if (typeof value === 'string' && value.includes('$')) {
		return value;
	}
	
	// Convert to number and format
	const numericValue = parseFloat(value);
	if (isNaN(numericValue)) return '';
	
	return formatMoney.format(numericValue);
};

/**
 * Normalize currency values for comparison by removing formatting
 * 
 * This function removes currency formatting (dollar signs, commas) to prepare
 * values for comparison or storage. Useful when comparing formatted values or
 * storing clean numeric values in the database.
 * 
 * @param {string|number} value - The value to normalize
 * @returns {string} Normalized value without currency formatting, always with 2 decimal places
 * 
 * @example
 * normalizeCurrencyValue("$1,234.56"); // "1234.56"
 * normalizeCurrencyValue(1234.56); // "1234.56"
 * normalizeCurrencyValue("$0.00"); // "0.00"
 * normalizeCurrencyValue("$1,234"); // "1234.00"
 */
export const normalizeCurrencyValue = (value) => {
	if (!value) return '0.00';
	
	// Remove currency formatting
	let cleanValue = value.toString().replace(/[$,]/g, '');
	
	// Parse as float to handle various input formats
	const numericValue = parseFloat(cleanValue);
	if (isNaN(numericValue)) return '0.00';
	
	// Format to always have 2 decimal places
	return numericValue.toFixed(2);
};

/**
 * Ensure currency value has proper decimal formatting for storage
 * 
 * This function ensures that currency values are properly formatted with
 * decimal places before saving to the database. It's specifically designed
 * for form submission where you want to ensure consistent decimal formatting.
 * 
 * @param {string|number} value - The value to format for storage
 * @returns {string} Value formatted with 2 decimal places
 * 
 * @example
 * ensureCurrencyDecimals("$1,234"); // "1234.00"
 * ensureCurrencyDecimals("$1,234.5"); // "1234.50"
 * ensureCurrencyDecimals("$1,234.56"); // "1234.56"
 */
export const ensureCurrencyDecimals = (value) => {
	if (!value) return '0.00';
	
	// Remove currency formatting and parse
	const cleanValue = value.toString().replace(/[$,]/g, '');
	const numericValue = parseFloat(cleanValue);
	
	if (isNaN(numericValue)) return '0.00';
	
	// Always return with 2 decimal places
	return numericValue.toFixed(2);
};

/**
 * Compare two currency values after normalization
 * 
 * This function compares two currency values by first normalizing them
 * (removing formatting) and then comparing the raw numeric values.
 * 
 * @param {string|number} value1 - First value to compare
 * @param {string|number} value2 - Second value to compare
 * @returns {boolean} Whether the values are equal after normalization
 * 
 * @example
 * compareCurrencyValues("$1,234.56", "1234.56"); // true
 * compareCurrencyValues("$1,234.56", "$1,234.57"); // false
 * compareCurrencyValues("$0.00", "0"); // true
 */
export const compareCurrencyValues = (value1, value2) => {
	const normalized1 = normalizeCurrencyValue(value1);
	const normalized2 = normalizeCurrencyValue(value2);
	return normalized1 === normalized2;
};

/**
 * Check if a value appears to be a currency field
 * 
 * This function detects if a value is likely a currency field by checking
 * for currency formatting indicators like dollar signs or commas.
 * 
 * @param {string|number} value - The value to check
 * @returns {boolean} Whether the value appears to be currency
 * 
 * @example
 * isCurrencyField("$1,234.56"); // true
 * isCurrencyField("1234.56"); // false
 * isCurrencyField("Some text"); // false
 * isCurrencyField(null); // false
 */
export const isCurrencyField = (value) => {
	if (!value) return false;
	return typeof value === 'string' && (value.includes('$') || value.includes(','));
}; 